<template>
  <div class="messages-wrapper">
    <div class="content-wrapper">
      <MessagesTaggingNavigationPanel />
      <div class="search-container">
        <CustomSearch placeholder="Find..." @update:modelValue="handleSearch" />
        <div v-if="searchResults.length > 0" class="search-navigation">
          <GreyArrowUpIcon24
            class="navigation-arrow"
            :class="{ 'disabled': currentSearchIndex === 0 }"
            @click="navigateToPreviousResult"
          />
          <span class="message-counter">{{ currentSearchPosition }} of {{ searchResults.length }}</span>
          <GreyArrowDownIcon24
            class="navigation-arrow"
            :class="{ 'disabled': currentSearchIndex === searchResults.length - 1 }"
            @click="navigateToNextResult"
          />
        </div>
      </div>
      <div ref="messagesContainer" class="scrollable-container">
        <div v-if="loadFlag" class="loader-bg">
          <div class="loader-p" />
        </div>
        <SetMessages
          ref="messagesList"
          :messages="messages"
          :message-search="keySearch"
          @changeCheckbox="changeMessageCheckbox"
        />
        <SelectedMessagesInfo
          :messages="messages"
          :selected-messages-id="selectedMessagesId"
          :timer-started="timeRemaining"
          @updatedActiveStatus="updatedActiveStatus"
          @exportMessages="openExportPopup"
        />
        <infinite-loading :identifier="infiniteId" @infinite="infiniteHandler">
          <div slot="spinner">
            Loading...
          </div>
          <div slot="no-more" />
          <div slot="no-results" />
        </infinite-loading>
      </div>
    </div>
    <MessageTaggingPanel @updateMessages="updateMessage" />
    <CommonDialog
      ref="progressDialog"
      :show-footer="false"
      :closable="false"
    >
      <h2 class="dialog-message-title">
        Exporting messages
      </h2>
      <p class="dialog-create-description" style="display:block; font-size: 14px;">
        Please wait a bit. The messages are
        being exported.
      </p>
      <div class="loader-p" />
    </CommonDialog>
    <SuccessPopup
      ref="successPopup"
      text="A URL will be sent to your email with a download link. Do not share this link."
    />
    <CommonDialog ref="exportV2SuccessPopup" :show-footer="false">
      <SuccessIcon />
      <h2 class="dialog-message-title">
        Success!
      </h2>
      <p class="dialog-message-description" style="display:block;">
        Chosen messages were exported, you can find them on case export page. Would you like to see exported files?
      </p>
      <div class="dialog-btn-wrapper">
        <button
          id="dialoge_proceed"
          class="btn btn-export"
          @click="goToExportPage"
        >
          <span class="dialog-btn-text">Continue</span>
        </button>
        <button
          id="dialoge_close"
          class="btn btn-close btn-revert"
          @click="closeSuccessExportV2Popup"
        >
          <span class="dialog-btn-text">Close</span>
        </button>
      </div>
    </CommonDialog>
    <ExportPopup
      ref="exportPopup"
      :is-filters-applied="false"
      :is-messages-export="true"
      @export="exportMessages"
    />
  </div>
</template>

<script>
import MessagesTaggingNavigationPanel
  from '@/components/cases/case-messages/message-tags/MessagesTaggingNavigationPanel.vue';
import MessageTaggingPanel from '@/components/cases/case-messages/message-tags/MessageTaggingPanel.vue';
import SetMessages from '@/components/cases/case-messages/SetMessages.vue';
import CustomSearch from '@/components/inputs/CustomSearch.vue';
import InfiniteLoading from 'vue-infinite-loading';
import SelectedMessagesInfo from '@/components/selected-messages-info/SelectedMessagesInfo.vue';
import CommonDialog from '@/components/popups/CommonDialog.vue';
import SuccessPopup from '@/components/popups/SuccessPopup.vue';
import streamReader from '@/mixins/streamReader';
import messagesTagging from '@/mixins/messagesTagging';
import exportTimer from '@/mixins/exportTimer';
import ExportPopup from '@/components/popups/ExportPopup.vue';
import SuccessIcon from '@/components/icons/SuccessIcon.vue';
import GreyArrowUpIcon24 from '@/components/icons/GreyArrowUpIcon24.vue';
import GreyArrowDownIcon24 from '@/components/icons/GreyArrowDownIcon24.vue';
import {mapActions, mapGetters} from 'vuex';

export default {
  name: 'MessagesTaggingMainTab',
  components: {
    SuccessIcon,
    ExportPopup,
    SuccessPopup, CommonDialog,
    SelectedMessagesInfo,
    CustomSearch,
    SetMessages,
    MessageTaggingPanel,
    MessagesTaggingNavigationPanel,
    InfiniteLoading,
    GreyArrowUpIcon24,
    GreyArrowDownIcon24,
  },
  mixins: [streamReader, messagesTagging, exportTimer],
  data() {
    return {
      infiniteId: +new Date(),
      loadFlag: true,
      isMessagesMounted: false,
      selectedMessagesId: [],
      selectedMessagesIdCopy: [],
      isCheckedAllMessages: false,
      format: '',
      caseId: this.$route.params.slug,
      searchResults: [],
      currentSearchIndex: 0,
    };
  },
  computed: {
    ...mapGetters('messagesTagging', {
      chatData: 'getChatData',
    }),
    currentSearchPosition() {
      return this.searchResults.length > 0 ? this.currentSearchIndex + 1 : 0;
    },
  },
  watch: {
    messages: {
      deep: true,
      handler() {
        if (this.isCheckedAllMessages) {
          this.messages.map(message => message.isActive = true);
        } else {
          this.messages.map(message => {
            const messageId = this.selectedMessagesId.find(id => id === message.id);
            message.isActive = !!messageId;
            return message;
          });
        }
      }
    }
  },
  mounted() {
    this.checkTimer('messagesExportEndTime');
    this.scrollToMessage();
  },
  methods: {
    ...mapActions('messagesTagging', {
      setSearchMessageId: 'setSearchMessageId',
      clearSearchMessageId: 'clearSearchMessageId',
      setTaggingMessageId: 'setTaggingMessageId',
    }),
    handleSearch(query) {
      this.keySearch = query;
      if (query) {
        this.debounce(() => this.searchMessages(query), 300);
      } else {
        this.searchResults = [];
        this.currentSearchIndex = 0;
        this.clearSearchMessageId();
      }
    },

    searchMessages(query) {
      this.$load(async () => {
        const res = await this.$api.cases.getCaseChats({
          caseId: this.caseId,
          chatId: this.chatData.chatId || '',
          clientId: '',
          keySearch: this.fixedEncodeURIComponent(query),
          dateFrom: '',
          dateTo: '',
        });

        this.searchResults = res.data.body
          .flatMap(client => client.chatRooms)
          .flatMap(chatRoom => chatRoom.lastMessageChatInfo);

        this.currentSearchIndex = 0;

        if (this.searchResults.length > 0) {
          this.navigateToResult(this.searchResults[0]);
        }
      }, (error) => {
        this.handleResponseError(error);
      });
    },

    navigateToPreviousResult() {
      if (this.searchResults.length > 0 && this.currentSearchIndex > 0) {
        this.currentSearchIndex--;
        this.navigateToResult(this.searchResults[this.currentSearchIndex]);
      }
    },

    navigateToNextResult() {
      if (this.searchResults.length > 0 && this.currentSearchIndex < this.searchResults.length - 1) {
        this.currentSearchIndex++;
        this.navigateToResult(this.searchResults[this.currentSearchIndex]);
      }
    },

    navigateToResult(message) {
      if (message) {
        // Set the search result as the new tagging message instead of additional active message
        this.setTaggingMessageId(message.messageId);
        // Clear search message ID to ensure only tagging message is active
        this.clearSearchMessageId();

        const isMessageLoaded = this.isMessageLoaded(message.messageId);

        if (isMessageLoaded) {
          this.scrollToMessage(message.messageId);
        } else {
          this.getMessageById(message.messageId);
          this.loadMoreUntilMessageFound(message.messageId);
        }
      }
    },

    isMessageLoaded(messageId) {
      return this.$refs.messagesList &&
        this.$refs.messagesList.getMessageRef &&
        this.$refs.messagesList.getMessageRef(messageId);
    },

    scrollToMessage(messageId = null) {
      this.$nextTick(() => {
        try {
          const targetMessageId = messageId || this.chatData.taggingMessageId;

          if (!targetMessageId) {
            console.warn('No message ID provided for scrolling');
            return;
          }

          const messageRef = this.$refs.messagesList.getMessageRef(targetMessageId);
          if (messageRef) {
            messageRef.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'nearest',
            });

            // Only set loadFlag to false if we're scrolling to the tagging message
            if (!messageId) {
              this.loadFlag = false;
            }
          } else {
            this.scrollToBottom();
            setTimeout(() => this.scrollToMessage(messageId), 100);
          }
        } catch (e) {
          console.error('Error scrolling to message:', e);
          setTimeout(() => this.scrollToMessage(messageId), 100);
        }
      });
    },

    async loadMoreUntilMessageFound(messageId) {
      if (!this.isMessageLoaded(messageId) && this.hasMoreMessages) {
        this.loadFlag = true;
        await this.getChatMessages();
        this.loadFlag = false;

        this.$nextTick(() => {
          if (this.isMessageLoaded(messageId)) {
            this.scrollToMessage(messageId);
          } else if (this.hasMoreMessages) {
            this.loadMoreUntilMessageFound(messageId);
          }
        });
      }
    },

    fixedEncodeURIComponent(str) {
      return str ? encodeURIComponent(str).replace(/[!'()*]/g, c => `%${c.charCodeAt(0).toString(16)}`) : '';
    },

    async infiniteHandler($state) {
      await this.getChatMessages();
      if (this.hasMoreMessages) {
        $state.loaded();
      } else {
        $state.complete();
      }
      this.loadFlag = false;
    },
    scrollToBottom() {
      this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight;
    },
    changeMessageCheckbox(item) {
      if (item.isActive) {
        this.selectedMessagesId.push(item.id);
      } else {
        this.selectedMessagesId = this.selectedMessagesId.filter(id => id !== item.id);
      }
    },
    updatedActiveStatus($event) {
      this.isCheckedAllMessages = $event.isCheckedAllMessages;
      this.selectedMessagesId = $event.selectedMessagesId;
      this.messages = $event.messages;
    },
    updateMessage(message) {
      this.getMessageById(message.id, true);
    },
    openExportPopup(format, data) {
      if (format) {
        this.format = format;
      }
      this.selectedMessagesIdCopy = data;
      this.$refs.exportPopup.openPopups();
    },
    exportMessages(format, config, applyTimeframes = false) {
      if (format === 'rsmf' || format === 'csv') {
        this.exportV1(format, applyTimeframes);
      } else if (format === 'pdf' || format === 'xml') {
        this.exportV2(format, config, applyTimeframes);
      }
    },
    exportV1(format) {
      this.$load(async () => {
        this.$refs.progressDialog.openPopups();
        const response = await this.$api.cases.exportMessagesV1(
          this.caseId,
          format,
          {messageIds: this.selectedMessagesIdCopy}
        );
        // eslint-disable-next-line no-undef
        const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
        await this.processStreamData(reader, 33);
        this.handleStreamResult();
        this.resetState();
        this.startExportTimer('messagesExportEndTime');
      }, (error) => {
        if (error.response) {
          this.handleResponseError(error);
          this.$emit('show-error', error.response.data.errors.message[0]);
        } else {
          this.$emit('show-error', error.data.errors.message[0]);
        }
      });
    },
    exportV2(format, config, applyTimeframes) {
      this.$load(async () => {
        this.$refs.progressDialog.openPopups();
        await this.$api.cases.exportMessagesV2(this.caseId, format, applyTimeframes, {
          config: config,
          messages: {messageIds: this.selectedMessagesIdCopy},
        })
          .then(async (response) => {
            // eslint-disable-next-line no-undef
            const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
            await this.processStreamData(reader, 33);
            this.handleStreamResult(false);
            this.$refs.exportV2SuccessPopup.openPopups();
            this.resetState();
            this.startExportTimer('messagesExportEndTime');
          }, (error) => {
            this.handleResponseError(error);
            if (error.response) {
              this.$emit('show-error', error.response.data.errors.message[0]);
            } else {
              this.$emit('show-error', error.data.errors.message[0]);
            }
          });
      });
    },
    goToExportPage() {
      this.$router.push({path: `/cases/${this.caseId}/export`});
    },
    closeSuccessExportV2Popup() {
      this.$refs.exportV2SuccessPopup.closePopups();
    },
    resetState() {
      this.selectedMessagesId = [];
      this.selectedMessagesIdCopy = [];
      this.isCheckedAllMessages = false;

      this.messages = this.messages.map(message => ({
        ...message,
        isActive: false,
      }));
    }
  },
};
</script>

<style scoped lang="scss">
.messages-wrapper {
  display: flex;
  margin: 25px 60px 0 40px;
  max-width: 90vw;

  .content-wrapper {
    padding: 50px 0 0;
    min-width: 600px;
    width: 100%;

    .search-container {
      display: flex;
      align-items: center;
      margin-bottom: 15px;
      position: relative;

      .search-navigation {
        display: flex;
        align-items: center;
        margin-left: 15px;
        max-width: 150px;
        width: 100%;

        .navigation-arrow {
          cursor: pointer;
          margin: 0 8px;

          &.disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
        }

        .message-counter {
          font-size: 12px;
          color: var(--secondary-text-color);
          margin: 0 8px;
        }
      }
    }

    .scrollable-container {
      max-height: 78.8vh;
      overflow-y: auto;
      scrollbar-color: var(--background-color) transparent;
      scrollbar-width: thin;
    }
  }

  .loader-p {
    width: 50px;
    height: 50px;
    position: relative;
    margin: 20px 40px 20px 0;
  }
}

.dialog-btn-wrapper {
  display: flex;
  align-items: center;
  flex-direction: row-reverse;
  gap: 20px;
}

.dialog-btn-wrapper .btn {
  height: 48px;
}

.dialog-btn-wrapper .btn-export {
  background-color: var(--accent-color);
  border: 1px solid var(--accent-color);
}

.dialog-btn-wrapper .btn-close:hover {
  background-color: var(--white-color);
}

.dialog-btn-wrapper .btn-export .dialog-btn-text {
  color: var(--white-color);
}

.dialog-btn-wrapper .btn-export:hover {
  color: var(--white-color);
}

.dialog-btn-wrapper .btn-export:hover {
  background-color: var(--accent-color);
  color: var(--white-color);
  border: 1px solid var(--accent-color);
}
</style>
